/**
 * Trading Decision Page — 2-column trading terminal layout
 * Left: Trade Ideas (actionable)  |  Right: Market context sidebar
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress, Skeleton, alpha,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Refresh as RefreshIcon,
  Gavel as GavelIcon,
  Warning,
  Lightbulb as LightbulbIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MarketRegimeWidget from './MarketRegimeWidget';
import TradeIdeasTable from './TradeIdeasTable';
import SectorRankingsWidget from './SectorRankingsWidget';
import SafeInvestRegimeCard from '../portfolio/SafeInvestRegimeCard';
import SectorRotationRadarWidget from './SectorRotationRadarWidget';
import StockCandidatesWidget from './StockCandidatesWidget';
import { tradingDecisionAPI, portfolioAPI } from '../../services/api';

/* ─── Skeleton ──────────────────────────────────────────────── */
const PageSkeleton = () => {
  const sk = { animation: 'wave' };
  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1.5, md: 2.5 }, pb: 4, pt: 1, minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 1 }} {...sk} />
          <Box>
            <Skeleton width={180} height={24} {...sk} />
            <Skeleton width={140} height={14} {...sk} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: 1 }} {...sk} />
          <Skeleton variant="rounded" width={140} height={32} sx={{ borderRadius: 1 }} {...sk} />
          <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: 1 }} {...sk} />
        </Box>
      </Box>
      {/* Status strip */}
      <Skeleton variant="rounded" height={38} sx={{ borderRadius: 1, mb: 2 }} {...sk} />
      {/* 2-col */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {/* TradeIdeasTable placeholder */}
          <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} {...sk} />
                <Skeleton width={120} height={18} {...sk} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.8 }}>
                <Skeleton variant="circular" width={28} height={28} {...sk} />
                <Skeleton variant="circular" width={28} height={28} {...sk} />
              </Box>
            </Box>
            <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
              {[0,1,2,3].map(i => <Skeleton key={i} variant="rounded" width={140} height={36} sx={{ borderRadius: 1.5 }} {...sk} />)}
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 1, py: 0.8, borderBottom: '1px solid #f1f5f9' }}>
                {Array(10).fill(0).map((_, i) => <Skeleton key={i} height={12} {...sk} />)}
              </Box>
              {Array(10).fill(0).map((_, r) => (
                <Box key={r} sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 1, py: 0.6 }}>
                  {Array(10).fill(0).map((_, i) => <Skeleton key={i} height={14} {...sk} />)}
                </Box>
              ))}
            </Box>
          </Box>
          {/* StockCandidatesWidget placeholder */}
          <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.2, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="rounded" width={28} height={28} sx={{ borderRadius: 1.5 }} {...sk} />
                <Box><Skeleton width={140} height={16} {...sk} /><Skeleton width={180} height={10} sx={{ mt: 0.4 }} {...sk} /></Box>
              </Box>
              <Skeleton variant="circular" width={28} height={28} {...sk} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '40px 70px 1fr 60px 70px 60px 60px 60px 60px', gap: 1.5, px: 2, py: 1, borderBottom: '1px solid #f1f5f9' }}>
              {Array(9).fill(0).map((_, i) => <Skeleton key={i} width="100%" height={12} {...sk} />)}
            </Box>
            {Array(8).fill(0).map((_, r) => (
              <Box key={r} sx={{ display: 'grid', gridTemplateColumns: '40px 70px 1fr 60px 70px 60px 60px 60px 60px', gap: 1.5, px: 2, py: 0.8 }}>
                {Array(9).fill(0).map((_, i) => <Skeleton key={i} width="100%" height={14} {...sk} />)}
              </Box>
            ))}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          {/* SafeInvestRegimeCard placeholder */}
          <Box sx={{ mb: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}><Skeleton width={70} height={14} {...sk} /><Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 0.5 }} {...sk} /></Box>
              <Skeleton width={50} height={22} {...sk} />
            </Box>
            <Box sx={{ px: 1.5, py: 0.6 }}><Skeleton width="80%" height={14} {...sk} /></Box>
            <Box sx={{ px: 1.5, py: 0.5 }}>{[0,1,2,3].map(i => <Skeleton key={i} height={20} sx={{ mb: 0.3 }} {...sk} />)}</Box>
            <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
              <Skeleton width={120} height={12} {...sk} /><Skeleton width={60} height={12} {...sk} />
            </Box>
          </Box>
          {/* MarketRegimeWidget placeholder */}
          <Box sx={{ mb: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}><Skeleton width={70} height={14} {...sk} /><Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 0.5 }} {...sk} /></Box>
              <Skeleton width={50} height={22} {...sk} />
            </Box>
            <Box sx={{ px: 1.5, py: 0.6 }}><Skeleton width="80%" height={14} {...sk} /></Box>
            <Box sx={{ px: 1.5, py: 0.5 }}>{[0,1,2,3].map(i => <Skeleton key={i} height={20} sx={{ mb: 0.3 }} {...sk} />)}</Box>
            <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
              <Skeleton width={60} height={12} {...sk} />
            </Box>
          </Box>
          {/* SectorRankingsWidget placeholder */}
          <Box sx={{ mb: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={26} height={26} sx={{ borderRadius: 1 }} {...sk} />
              <Skeleton width={110} height={16} {...sk} />
            </Box>
            <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
              {Array(11).fill(0).map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.3 }} {...sk} />)}
            </Box>
          </Box>
          {/* SectorRotationRadarWidget placeholder */}
          <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="rounded" width={26} height={26} sx={{ borderRadius: 1 }} {...sk} />
              <Skeleton width={120} height={16} {...sk} />
            </Box>
            <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
              {Array(11).fill(0).map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.3 }} {...sk} />)}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// ══════════════════════════════════════
const TradingDecisionPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calcDate, setCalcDate] = useState(null);
  const [nav, setNav] = useState(1000000000);
  const [regime, setRegime] = useState(null);
  const [tradeIdeasCount, setTradeIdeasCount] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tradeIdeasData, setTradeIdeasData] = useState([]);
  const [safeInvestRegime, setSafeInvestRegime] = useState(null);
  const [safeInvestEnabled, setSafeInvestEnabled] = useState(true);
  const [safeInvestLoading, setSafeInvestLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setCalcDate(new Date().toISOString().split('T')[0]);
    portfolioAPI.getSafeInvestRegime()
      .then(r => r.data.success && setSafeInvestRegime(r.data.data))
      .catch(() => setSafeInvestEnabled(false))
      .finally(() => setSafeInvestLoading(false));
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true); setError(null);
    try {
      const r = await tradingDecisionAPI.getDecision(calcDate, nav);
      setRegime(r.data.regime);
      const ideas = r.data.trade_ideas || [];
      setTradeIdeasData(ideas);
      setTradeIdeasCount(ideas.length);
      setLastRefresh(new Date());
    } catch (err) { setError(err.message || 'Failed to refresh'); }
    finally { setRefreshing(false); setLoading(false); }
  }, [calcDate, nav]);

  useEffect(() => { if (calcDate) handleRefresh(); }, [calcDate, handleRefresh]);

  // Quick metrics for status strip
  const breakoutCount = tradeIdeasData.filter(i => i.reason === 'breakout').length;
  const pullbackCount = tradeIdeasData.filter(i => i.reason === 'pullback').length;
  const avgRisk = tradeIdeasCount > 0 ? ((tradeIdeasData.reduce((s, i) => s + (i.risk_pct || 0), 0) / tradeIdeasCount) * 100).toFixed(1) : '0';

  // Regime
  const regimeLabel = safeInvestRegime?.regime || regime || '—';
  const regimeScore = safeInvestRegime?.market_score ?? null;
  const regimeColor = regimeLabel === 'RISK_ON' || regimeLabel === 'ALLOW' ? '#10b981'
    : regimeLabel === 'RECOVERY' || regimeLabel === 'WATCH' ? '#f59e0b'
    : regimeLabel === 'RISK_OFF' || regimeLabel === 'AVOID' ? '#ef4444' : '#64748b';

  if (loading) return <PageSkeleton />;

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1.5, md: 2.5 }, pb: 4, pt: 1 }}>

      {/* ═══ HEADER ═══ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.25)}`,
          }}>
            <GavelIcon sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2, letterSpacing: -0.3 }}>
              {t('tradingDecision.title', 'Quyết Định Giao Dịch')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {t('tradingDecision.subtitle', 'Regime · Ngành · Entry · Risk · Size')}
              {lastRefresh && <span style={{ marginLeft: 4, fontSize: 10, color: '#b0b8c4' }}>· {lastRefresh.toLocaleTimeString()}</span>}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" type="date" value={calcDate || ''}
            onChange={e => setCalcDate(e.target.value.split('T')[0])}
            sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: 1, height: 32, fontSize: 12 } }} />
          <TextField size="small" type="number" value={nav}
            onChange={e => setNav(parseFloat(e.target.value) || 0)}
            InputProps={{
              inputProps: { min: 0, step: 100000000 },
              endAdornment: <Typography sx={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap' }}>VND</Typography>,
            }}
            sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1, height: 32, fontSize: 12 } }} />
          <Button variant="contained" size="small"
            startIcon={refreshing ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={handleRefresh} disabled={refreshing || !calcDate}
            sx={{
              px: 1.5, fontSize: 11, fontWeight: 700, height: 32,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              boxShadow: `0 2px 6px ${alpha('#667eea', 0.2)}`,
              textTransform: 'none', borderRadius: 1,
            }}>
            {refreshing ? '...' : t('tradingDecision.refreshDecision', 'Phân Tích')}
          </Button>
        </Box>
      </Box>

      {/* ═══ STATUS STRIP ═══ */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
        px: 1.5, py: 0.75, borderRadius: 1,
        bgcolor: alpha(regimeColor, 0.04), border: `1px solid ${alpha(regimeColor, 0.12)}`,
        flexWrap: 'wrap',
      }}>
        <Chip size="small" label={regimeLabel.replace('_', ' ')}
          sx={{
            fontWeight: 800, fontSize: 11, height: 24, letterSpacing: 0.5,
            bgcolor: regimeColor, color: '#fff',
            '& .MuiChip-label': { px: 1 },
          }} />
        {regimeScore != null && (
          <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>
            {t('tradingDecision.statusStrip.score', 'Score')} <strong style={{ color: regimeColor }}>{regimeScore}</strong>/100
          </Typography>
        )}
        <Box sx={{ width: '1px', height: 16, bgcolor: '#e2e8f0', mx: 0.5, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
          <LightbulbIcon sx={{ fontSize: 12, color: '#667eea', verticalAlign: 'middle', mr: 0.3 }} />
          <strong style={{ color: '#1e293b' }}>{tradeIdeasCount}</strong> {t('tradingDecision.statusStrip.ideas', 'ideas')}
          <span style={{ color: '#94a3b8', margin: '0 4px' }}>·</span>
          {breakoutCount} {t('tradingDecision.statusStrip.breakout', 'breakout')} · {pullbackCount} {t('tradingDecision.statusStrip.pullback', 'pullback')}
        </Typography>
        <Box sx={{ width: '1px', height: 16, bgcolor: '#e2e8f0', mx: 0.5, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: parseFloat(avgRisk) > 5 ? '#ef4444' : parseFloat(avgRisk) > 3 ? '#f59e0b' : '#10b981' }}>
          <Warning sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
          {t('tradingDecision.statusStrip.avgRisk', 'Avg Risk')} {avgRisk}%
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Box sx={{ mb: 1.5, px: 1.5, py: 0.75, borderRadius: 1,
          bgcolor: alpha('#ef4444', 0.05), border: `1px solid ${alpha('#ef4444', 0.12)}`,
          display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} />
          <Typography sx={{ fontSize: 11, color: '#ef4444', fontWeight: 500 }}>{error}</Typography>
        </Box>
      )}

      {/* ═══ MAIN 2-COL LAYOUT ═══ */}
      <Grid container spacing={2}>
        {/* LEFT: Actionable content */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box sx={{ mb: 2 }}>
            <TradeIdeasTable calcDate={calcDate} nav={nav} onRefresh={setTradeIdeasCount} />
          </Box>
          <StockCandidatesWidget topK={15} />
        </Grid>

        {/* RIGHT: Context sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          {safeInvestEnabled && safeInvestLoading && (
            <Box sx={{ mb: 2, borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}><Skeleton width={70} height={14} animation="wave" /><Skeleton variant="rounded" width={60} height={20} sx={{ borderRadius: 0.5 }} animation="wave" /></Box>
                <Skeleton width={50} height={22} animation="wave" />
              </Box>
              <Box sx={{ px: 1.5, py: 0.6 }}><Skeleton width="80%" height={14} animation="wave" /></Box>
              <Box sx={{ px: 1.5, py: 0.5 }}>{[0,1,2,3].map(i => <Skeleton key={i} height={20} sx={{ mb: 0.3 }} animation="wave" />)}</Box>
              <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                <Skeleton width={120} height={12} animation="wave" /><Skeleton width={60} height={12} animation="wave" />
              </Box>
            </Box>
          )}
          {safeInvestEnabled && safeInvestRegime && (
            <Box sx={{ mb: 2 }}>
              <SafeInvestRegimeCard regimeData={safeInvestRegime} />
            </Box>
          )}
          <Box sx={{ mb: 2 }}>
            <MarketRegimeWidget calcDate={calcDate} onRegimeChange={setRegime} />
          </Box>
          <Box sx={{ mb: 2 }}>
            <SectorRankingsWidget calcDate={calcDate} />
          </Box>
          {safeInvestEnabled && (
            <Box sx={{ mb: 2 }}>
              <SectorRotationRadarWidget calcDate={calcDate} />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Disclaimer */}
      <Box sx={{ mt: 1, px: 1.5, py: 0.75, borderRadius: 1, bgcolor: alpha('#667eea', 0.03), border: `1px solid ${alpha('#667eea', 0.06)}` }}>
        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
          <span style={{ fontWeight: 700, color: '#667eea' }}>{t('tradingDecision.infoTitle', 'Lưu ý')}</span>
          {': '}{t('tradingDecision.infoDescription', 'Đây là công cụ hỗ trợ phân tích, không phải khuyến nghị đầu tư. Luôn tự nghiên cứu trước khi giao dịch.')}
        </Typography>
      </Box>
    </Box>
  );
};

export default TradingDecisionPage;
